/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  adminAcceptRoadmapTriage,
  adminActivityActions,
  adminActivityAnalytics,
  adminAddRoadmapBlocker,
  adminAddRoadmapComment,
  adminCloseRoadmapTriage,
  adminCreateRoadmapItem,
  adminCreateTeam,
  adminDeleteRoadmapBlocker,
  adminDeleteRoadmapComment,
  adminDeleteRoadmapItem,
  adminDeleteRoadmapTriage,
  adminDeleteTeam,
  adminDeleteUser,
  adminFollowRoadmapItem,
  adminGetRoadmap,
  adminGetUser,
  adminListActivity,
  adminListRoadmapComments,
  adminListTeams,
  adminListUsers,
  adminMcpBlockUser,
  adminMcpCalls,
  adminMcpIssue,
  adminMcpIssueAction,
  adminMcpIssueNote,
  adminMcpIssues,
  adminMcpIssuesAttention,
  adminMcpSpeed,
  adminMcpUnblockUser,
  adminMcpUsage,
  adminMcpUsers,
  adminMergeRoadmapTriage,
  adminProposeRoadmapIdea,
  adminResolveRoadmapBlocker,
  adminRevokeUserSessions,
  adminSetPersonOverrides,
  adminSetRoadmapBudget,
  adminSetRoadmapEffort,
  adminSetRoadmapStatus,
  adminSetTeamMembers,
  adminSetToolEnabled,
  adminUpdateRoadmapItem,
  adminUpdateTeam,
  adminUpdateUser,
  type AppJob,
  type AppSnapshotSummary,
  type AppsOverview,
  type ConnectorOverview,
  getActivity,
  getBezettingJob,
  getBezettingSection,
  getBezettingSnapshot,
  getConnectorOverview,
  getHealth,
  getMaandrapportDetail,
  type GetMaandrapportDetailResponse,
  getMaandrapportJob,
  getMaandrapportSection,
  getMaandrapportSnapshot,
  getMaandrapportView,
  getMe,
  getMeDetail,
  getToolDefinition,
  type IssueStatusAction,
  listApps,
  listBezettingMeldingen,
  logout,
  type MaandrapportView,
  markBezettingMeldingenRead,
  markBezettingMeldingenUnread,
  type McpConnectorUser,
  type McpIssueDetail,
  type McpUserBlockStatus,
  type MeldingenList,
  type MeldingenUpdate,
  type PersonOverrideSetting,
  type RecomputeAccepted,
  recomputeBezetting,
  recomputeMaandrapport,
  type RoadmapBlockerCreate,
  type RoadmapComment,
  type RoadmapCommentCreate,
  type RoadmapDeveloperUpdate,
  type RoadmapIdeaProposal,
  type RoadmapItem,
  type RoadmapItemCreate,
  type RoadmapItemUpdate,
  type RoadmapMonth,
  type RoadmapOverview,
  type RoadmapStatusChange,
  setPersonalToolEnabled,
  type StartViewAs,
  startViewAs,
  stopViewAs,
  type TeamsOverview,
  type TeamUpdate,
  updateProfile,
  viewAsOptions,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";
import { isDeveloper } from "@/lib/roles";

import { type ApiError, followViewAs, isAuthError } from "./client";
import { withBlockStatus, withPersonBlock, withTool } from "./connector-cache";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

/**
 * While the backend is failing, `useMe` re-checks the session after 3 s, then
 * doubles the wait after every failed re-check, up to 30 s.
 */
const RECONNECT_INTERVAL_MS = 3_000;
const RECONNECT_MAX_INTERVAL_MS = 30_000;
/** Each query's `errorUpdateCount` at its last success: it never resets itself. */
const errorsBeforeOutage = new WeakMap<object, number>();

/**
 * The signed-in user's profile. Only a 401/403 means "signed out" (the route
 * guards redirect to /login on it, and the query client never retries it).
 * Any other failure — the backend restarting under `make dev`, a proxy 502,
 * Supabase down — is retried by the query client and then re-checked, more
 * slowly the longer it lasts, until the backend answers, so the guards recover
 * on their own.
 *
 * Every answer sets whether requests carry the view-as header
 * (`followViewAs`). For a developer, and during any view, the window regaining
 * focus checks the session again, so a tab learns soon that a view started,
 * switched or ended in another tab. Everyone else keeps the default.
 */
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      followViewAs(data?.view_as);
      return data;
    },
    refetchOnWindowFocus: (query) => {
      const me = query.state.data;
      return me?.view_as || isDeveloper(me?.role) ? "always" : false;
    },
    refetchInterval: (query) => {
      const { status, error, errorUpdateCount } = query.state;
      if (status === "success") {
        errorsBeforeOutage.set(query, errorUpdateCount);
      }
      if (status !== "error" || isAuthError(error)) {
        return false;
      }
      const failures = errorUpdateCount - (errorsBeforeOutage.get(query) ?? 0);
      return Math.min(
        RECONNECT_INTERVAL_MS * 2 ** Math.max(0, failures - 1),
        RECONNECT_MAX_INTERVAL_MS,
      );
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

/** The account's details; off for a view as a role, which has no account (404). */
export function useMeDetail({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
    enabled,
  });
}

export function useActivity({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ─── View as (developers) ────────────────────────────────────────────────

/**
 * Whom a developer can view the dashboard as: everyone with a dashboard
 * account but themselves, and the roles. The server checks the real
 * identity, so this also answers while a view is on (the bar's Wissel).
 */
export function useViewAsOptions({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["view-as", "options"],
    queryFn: async () => {
      const { data } = await viewAsOptions();
      return data;
    },
    enabled,
  });
}

/**
 * Starts viewing the dashboard as one person or one role, or switches the
 * view that is on. Nothing is invalidated: the caller reloads the page
 * (`reloadDashboard`), since every query, the session's own included, now
 * answers for someone else.
 */
export function useStartViewAs() {
  return useMutation({
    mutationFn: async (target: StartViewAs) => {
      const { data } = await startViewAs({ body: target });
      return data;
    },
  });
}

/** Stops the view that is on; the caller reloads the page, as after a start. */
export function useStopViewAs() {
  return useMutation({
    mutationFn: async () => {
      await stopViewAs();
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
      refetchAfterRoleChange(queryClient);
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      refetchAfterRoleChange(queryClient);
    },
  });
}

/**
 * After a user's role changed or the user was deleted: the Admins team counts
 * every dashboard admin as a member, so refetch the Teams page and the apps
 * the admin may open (the sidebar's Apps group).
 */
function refetchAfterRoleChange(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: MCP_TEAMS_KEY });
  void queryClient.invalidateQueries({ queryKey: APPS_KEY, exact: true });
}

/** Ends every active session of a user: "Sign out everywhere". */
export function useRevokeUserSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminRevokeUserSessions({ path: { user_id: vars.userId } });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
      // The log entry is recorded on the acting admin; refresh the activity feed.
      void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  /** Actions by their code; none for every action. */
  actions?: readonly string[];
  /** Dashboard users by id; none for everyone. */
  userIds?: readonly string[];
  /** The first moment (ISO with an offset); none for no lower bound. */
  from?: string;
  /** The last moment, included; none for up to now. */
  to?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /**
   * For a sort by action: the actions in the order their words read in the
   * dashboard's language (`labelOrder`), the ones it leaves out after them.
   */
  sortOrder?: readonly string[];
  page?: number;
  pageSize?: number;
}

/**
 * A page of the activity log, filtered (a search, people, actions, a time
 * range), sorted and paged on the server.
 */
export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.actions?.length ? [...params.actions] : undefined,
          user_id: params.userIds?.length ? [...params.userIds] : undefined,
          from: params.from,
          to: params.to,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          sort_order: params.sortOrder?.length ? [...params.sortOrder] : undefined,
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
    // Another period keeps the figures on screen until its own arrive.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── MCP connector overview ──────────────────────────────────────────────

const MCP_OVERVIEW_KEY = ["mcp", "overview"];
const MCP_OVERVIEW_STALE_MS = 5 * 60_000;
/** How soon to ask again when the backend could not read the tool switches. */
const MCP_SWITCHES_RETRY_MS = 30_000;

/**
 * Whether the overview holds switches the backend could not read: the
 * caller's own (`personal_switches_known`), or the developers' switches for
 * everyone (`tool_switches_known`). Every role gets both, since a paused tool
 * shows as on to anyone while those switches are unread.
 */
function switchesUnread(overview: ConnectorOverview | undefined): boolean {
  return overview?.personal_switches_known === false || overview?.tool_switches_known === false;
}

/**
 * The connector at a glance, for any signed-in user: how it runs, the URL to
 * add in Claude, whether the Odoo tools are on, the tool groups, and every tool
 * with its title, one-line summary, group, read or write, whether it is
 * available or coming soon, whether a developer paused it for everyone, the
 * caller's own switch and whether Claude gets it; for the admin roles also who
 * each tool is for, and for a developer the switch for everyone itself. It
 * changes when the backend restarts or someone switches a tool
 * (`useSetToolEnabled` and `useSetPersonalToolEnabled` refresh it then), so it
 * is kept for a few minutes rather than refetched on every visit. While the
 * backend could not read the switches (`switchesUnread`), it is never fresh
 * and is asked again every 30 s (the sidebar keeps it in use on every page),
 * so the Tools page's "try again in a minute" comes true on its own.
 */
export function useConnectorOverview() {
  return useQuery({
    queryKey: MCP_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getConnectorOverview();
      logger.debug(
        "[connector]",
        "overview",
        data?.mode,
        data?.odoo_tools_on,
        switchesUnread(data) ? "switches unknown" : "switches read",
      );
      return data;
    },
    staleTime: (query) => (switchesUnread(query.state.data) ? 0 : MCP_OVERVIEW_STALE_MS),
    refetchInterval: (query) => (switchesUnread(query.state.data) ? MCP_SWITCHES_RETRY_MS : false),
  });
}

/**
 * One tool exactly as DC Data lists it to Claude (`GET
 * /mcp/tools/{name}/definition`): its description, its input schema and its
 * parameters, and its size. Only for a served tool on the caller's own list;
 * any other name answers 404 `tool_not_found`, which is not asked again. Null
 * asks nothing: the Tools page asks only while a tool's dialog is open, and
 * never for a tool coming soon. A definition changes only with a release, so
 * it is kept like the overview.
 */
export function useToolDefinition(name: string | null) {
  return useQuery({
    queryKey: ["mcp", "tools", name, "definition"],
    queryFn: async () => {
      logger.debug("[connector]", "tool definition", name);
      const { data } = await getToolDefinition({ path: { name: name ?? "" } });
      return data;
    },
    enabled: name !== null,
    staleTime: MCP_OVERVIEW_STALE_MS,
    retry: (failureCount, error) =>
      !isAuthError(error) && (error as Partial<ApiError>).status !== 404 && failureCount < 2,
  });
}

// ─── Admin: MCP connector ────────────────────────────────────────────────

const MCP_USERS_KEY = ["admin", "mcp", "users"];

/**
 * After a switch for everyone, success or not: refetch the overview (the
 * Tools page, Home, the sidebar, Docs and Analytics all read it) and the
 * activity feed (the change is logged on the acting developer). Also after a
 * failure, whose write may have landed before the response was lost.
 */
function refetchAfterSwitch(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: MCP_OVERVIEW_KEY });
  void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
}

/**
 * A developer's switch of one of the connector's tools, on or off for
 * everyone in Claude (not `whoami`; the backend refuses every other role with
 * `developer_required`). The server's answer goes into the overview at once,
 * so the Tools page shows the new switch the moment the request returns; then
 * the overview and the activity feed are refetched (`refetchAfterSwitch`).
 */
export function useSetToolEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; enabled: boolean }) => {
      logger.debug("[connector]", "switch tool", vars.name, vars.enabled ? "on" : "off");
      const { data } = await adminSetToolEnabled({
        path: { name: vars.name },
        body: { enabled: vars.enabled },
      });
      return data;
    },
    onSuccess: (tool) => {
      if (tool) {
        queryClient.setQueryData<ConnectorOverview>(MCP_OVERVIEW_KEY, (overview) =>
          withTool(overview, tool),
        );
      }
    },
    onSettled: () => refetchAfterSwitch(queryClient),
  });
}

/**
 * Switches one tool on or off in Claude for the signed-in person alone ("Voor
 * mij" on the Tools page; not `whoami`). Like `useSetToolEnabled`: the tool
 * the server returns goes into the overview at once, then the overview is
 * refetched, also after a failure, whose write may have landed. Nothing goes
 * to the activity log: it is a personal preference.
 */
export function useSetPersonalToolEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; enabled: boolean }) => {
      logger.debug("[connector]", "switch tool for me", vars.name, vars.enabled ? "on" : "off");
      const { data } = await setPersonalToolEnabled({
        path: { name: vars.name },
        body: { enabled: vars.enabled },
      });
      return data;
    },
    onSuccess: (tool) => {
      if (tool) {
        queryClient.setQueryData<ConnectorOverview>(MCP_OVERVIEW_KEY, (overview) =>
          withTool(overview, tool),
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MCP_OVERVIEW_KEY });
    },
  });
}

/**
 * Connector usage over the last `days` calendar days (Belgian time). `enabled`
 * false skips the request: the home page asks only for admins.
 */
export function useAdminMcpUsage(days: number, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "mcp", "usage", days],
    queryFn: async () => {
      const { data } = await adminMcpUsage({ query: { days } });
      return data;
    },
    enabled,
    // Keep the previous period on screen (dimmed) while the next one loads.
    placeholderData: (prev) => prev,
  });
}

/**
 * The connector's speed over the last `days` calendar days (Belgian time): per
 * day the waits as a share of the Odoo calls' time, the p95 wait, Odoo's p95
 * slowest request and the rate-limited calls, and the same per tool.
 */
export function useAdminMcpSpeed(days: number) {
  return useQuery({
    queryKey: ["admin", "mcp", "speed", days],
    queryFn: async () => {
      const { data } = await adminMcpSpeed({ query: { days } });
      return data;
    },
    // Keep the previous period on screen (dimmed) while the next one loads.
    placeholderData: (prev) => prev,
  });
}

/** Every issue key below `admin/mcp/issues`, so one invalidation reaches the list, the home page's and each issue. */
const MCP_ISSUES_KEY = ["admin", "mcp", "issues"];

/**
 * Every issue (failed calls bundled by tool and error kind) with a failure in
 * the last `days` calendar days, with its status, its failures per day and
 * its people. Unpaged: the Fouten page sorts and filters them, and the home
 * page counts what its links to it open. `enabled` false skips the request.
 */
export function useAdminMcpIssues(days: number, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...MCP_ISSUES_KEY, "list", days],
    queryFn: async () => {
      const { data } = await adminMcpIssues({ query: { days } });
      return data;
    },
    placeholderData: (prev) => prev,
    enabled,
  });
}

/**
 * The issues still to analyse (new, open, or resolved and failed again), with
 * their failures over the last 7 days, and how many a developer acknowledged:
 * the home page's list. `enabled` false skips the request: only admins ask.
 */
export function useAdminMcpIssuesAttention({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...MCP_ISSUES_KEY, "attention"],
    queryFn: async () => {
      const { data } = await adminMcpIssuesAttention();
      return data;
    },
    enabled,
  });
}

/** One issue's query key: its key and the period its figures cover. */
function issueKey(key: string, days: number) {
  return [...MCP_ISSUES_KEY, "detail", key, days];
}

/**
 * One issue over the last `days` calendar days: its figures, its failures per
 * day and its history (the developers' status changes and every admin role's
 * notes). A key no kept call failed under answers 404 `tool_issue_not_found`,
 * which is not retried.
 */
export function useAdminMcpIssue(key: string, days: number) {
  return useQuery({
    queryKey: issueKey(key, days),
    queryFn: async () => {
      const { data } = await adminMcpIssue({ path: { key }, query: { days } });
      return data;
    },
    placeholderData: (prev) => prev,
    retry: (count, error) => (error as { status?: number }).status !== 404 && count < 2,
  });
}

/** A developer's change of an issue's status, with its optional note. */
export interface McpIssueActionVars {
  key: string;
  action: IssueStatusAction;
  note?: string;
  /** The period the issue page shows, so the answer replaces what is on screen. */
  days: number;
}

/**
 * A developer acknowledges, resolves, ignores or reopens an issue, with an
 * optional note. The answer (the issue as it is after, its history included)
 * replaces the issue on screen at once; then the issue lists, the home page's
 * and the activity feed (a status change is logged on the developer) refetch.
 */
export function useMcpIssueAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: McpIssueActionVars) => {
      logger.debug("[issues]", vars.action, vars.key);
      const { data } = await adminMcpIssueAction({
        path: { key: vars.key },
        query: { days: vars.days },
        body: { action: vars.action, note: vars.note?.trim() || null },
      });
      return data;
    },
    onSuccess: (issue: McpIssueDetail | undefined, vars) => {
      if (issue) {
        queryClient.setQueryData(issueKey(vars.key, vars.days), issue);
      }
      void queryClient.invalidateQueries({ queryKey: MCP_ISSUES_KEY });
      void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
    },
  });
}

/** A note on an issue, from any admin role. */
export interface McpIssueNoteVars {
  key: string;
  note: string;
  /** The period the issue page shows, so the answer replaces what is on screen. */
  days: number;
}

/**
 * Any admin role adds a note to an issue's history; its status stays. The
 * answer (the issue with the note in its history) replaces the issue on
 * screen at once; then the issues refetch. A note writes nothing to the
 * activity feed, so that stays.
 */
export function useMcpIssueNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: McpIssueNoteVars) => {
      logger.debug("[issues] note", vars.key);
      const { data } = await adminMcpIssueNote({
        path: { key: vars.key },
        query: { days: vars.days },
        body: { note: vars.note.trim() },
      });
      return data;
    },
    onSuccess: (issue: McpIssueDetail | undefined, vars) => {
      if (issue) {
        queryClient.setQueryData(issueKey(vars.key, vars.days), issue);
      }
      void queryClient.invalidateQueries({ queryKey: MCP_ISSUES_KEY });
    },
  });
}

/** Everyone seen by the connector plus everyone blocked, with their block. */
export function useAdminMcpUsers() {
  return useQuery({
    queryKey: MCP_USERS_KEY,
    queryFn: async () => {
      const { data } = await adminMcpUsers();
      return data ?? [];
    },
  });
}

/** A column the call log sorts on, on the server. */
export type McpCallSortKey = "created_at" | "tool" | "user" | "duration" | "status";

export interface AdminMcpCallsParams {
  /** The last `days` calendar days; left out for a custom range. */
  days?: number;
  /** A custom range's first and last moment (ISO with an offset), the end included. */
  from?: string;
  to?: string;
  /** People by their Microsoft object id; none for everyone. */
  userOids?: readonly string[];
  /** Tools by name; none for every tool. */
  tools?: readonly string[];
  outcome?: "ok" | "failed";
  /** Only an issue's failed calls. */
  issueKey?: string;
  sortBy?: McpCallSortKey;
  sortDir?: "asc" | "desc";
  /**
   * For a sort by tool or status: the tools, or `ok` and the error kinds, in
   * the order of the words the table shows (`callSortOrder`), the ones it
   * leaves out after them.
   */
  sortOrder?: readonly string[];
  limit?: number;
  offset?: number;
}

/**
 * A page of the connector's tool calls (metadata only), filtered, sorted and
 * paged on the server: newest first by default. Each call with its person's
 * name, its issue and its Odoo figures.
 */
export function useAdminMcpCalls(params: AdminMcpCallsParams) {
  return useQuery({
    queryKey: ["admin", "mcp", "calls", params],
    queryFn: async () => {
      const { data } = await adminMcpCalls({
        query: {
          days: params.days,
          from: params.from,
          to: params.to,
          user_oid: params.userOids?.length ? [...params.userOids] : undefined,
          tool: params.tools?.length ? [...params.tools] : undefined,
          outcome: params.outcome,
          issue_key: params.issueKey,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          sort_order: params.sortOrder?.length ? [...params.sortOrder] : undefined,
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * After a block or unblock: write the block the server confirmed into the
 * users list and the Teams page's people at once, so the row is right the
 * moment the dialog closes, then refetch both and the activity feed (the
 * action is logged on the acting admin) in the background, and the usage,
 * whose people carry their block.
 */
function applyMcpBlockStatus(
  queryClient: ReturnType<typeof useQueryClient>,
  status: McpUserBlockStatus | undefined,
) {
  if (status) {
    queryClient.setQueryData<McpConnectorUser[]>(MCP_USERS_KEY, (users) =>
      withBlockStatus(users, status),
    );
    queryClient.setQueryData<TeamsOverview>(MCP_TEAMS_KEY, (teams) =>
      withPersonBlock(teams, status),
    );
  }
  void queryClient.invalidateQueries({ queryKey: MCP_TEAMS_KEY });
  void queryClient.invalidateQueries({ queryKey: MCP_USERS_KEY });
  void queryClient.invalidateQueries({ queryKey: ["admin", "mcp", "usage"] });
  void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
}

/**
 * Removes a person's connector access on Velta's side. The MCP server applies
 * it within its block-list cache time (about 30 s).
 */
export function useBlockMcpUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userOid: string }) => {
      logger.debug("[connector]", "block", vars.userOid);
      const { data } = await adminMcpBlockUser({ path: { user_oid: vars.userOid } });
      return data;
    },
    onSuccess: (status) => applyMcpBlockStatus(queryClient, status),
  });
}

/** Gives a blocked person their connector access back (within about 30 s). */
export function useUnblockMcpUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userOid: string }) => {
      logger.debug("[connector]", "unblock", vars.userOid);
      const { data } = await adminMcpUnblockUser({ path: { user_oid: vars.userOid } });
      return data;
    },
    onSuccess: (status) => applyMcpBlockStatus(queryClient, status),
  });
}

// ─── Admin: MCP connector teams ──────────────────────────────────────────

const MCP_TEAMS_KEY = ["admin", "mcp", "teams"];

/**
 * After any change to the teams or a person's exceptions, success or not:
 * refetch the Teams page and the activity feed (every change is logged on the
 * acting admin), and the apps the admin may open, which the same access
 * decides (the sidebar's Apps group). Also after a failure, whose write may
 * have landed before the response was lost.
 */
function refetchAfterTeamChange(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: MCP_TEAMS_KEY });
  void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
  void queryClient.invalidateQueries({ queryKey: APPS_KEY, exact: true });
}

/**
 * Everything the Teams page shows: the teams admins created (by name), the
 * default team for everyone in no team, every person's exceptions and the
 * people the dashboard knows.
 */
export function useAdminTeams() {
  return useQuery({
    queryKey: MCP_TEAMS_KEY,
    queryFn: async (): Promise<TeamsOverview | undefined> => {
      const { data } = await adminListTeams();
      logger.debug("[teams]", "overview", data?.teams.length, "teams");
      return data;
    },
  });
}

/** Creates a team with its name, description and group parts; members follow separately. */
export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; description: string | null; grants: string[] }) => {
      logger.debug("[teams]", "create", vars.name);
      const { data } = await adminCreateTeam({ body: vars });
      return data;
    },
    onSettled: () => refetchAfterTeamChange(queryClient),
  });
}

/**
 * Changes a team's name, description or group parts: only the fields given.
 * The default team takes `grants` alone.
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { teamId: string; change: TeamUpdate }) => {
      logger.debug("[teams]", "update", vars.teamId, Object.keys(vars.change));
      const { data } = await adminUpdateTeam({
        path: { team_id: vars.teamId },
        body: vars.change,
      });
      return data;
    },
    onSettled: () => refetchAfterTeamChange(queryClient),
  });
}

/** Sets a team's members: the full list of emails. */
export function useSetTeamMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { teamId: string; members: string[] }) => {
      logger.debug("[teams]", "members", vars.teamId, vars.members.length);
      const { data } = await adminSetTeamMembers({
        path: { team_id: vars.teamId },
        body: { members: vars.members },
      });
      return data;
    },
    onSettled: () => refetchAfterTeamChange(queryClient),
  });
}

/** Deletes a team (never the default team); its members fall back on their other teams. */
export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { teamId: string }) => {
      logger.debug("[teams]", "delete", vars.teamId);
      await adminDeleteTeam({ path: { team_id: vars.teamId } });
    },
    onSettled: () => refetchAfterTeamChange(queryClient),
  });
}

/** Sets one person's exceptions: the full list; an empty list removes them all. */
export function useSetPersonOverrides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; overrides: PersonOverrideSetting[] }) => {
      logger.debug("[teams]", "exceptions", vars.overrides.length);
      const { data } = await adminSetPersonOverrides({
        path: { email: vars.email },
        body: { overrides: vars.overrides },
      });
      return data ?? [];
    },
    onSettled: () => refetchAfterTeamChange(queryClient),
  });
}

// ─── Admin: roadmap ──────────────────────────────────────────────────────

/** Every month's roadmap overview: a change to an item or a budget makes each of them stale. */
export const ROADMAP_KEY = ["admin", "roadmap"] as const;

/** The overview with one month's figures; null for the current month in Belgium. */
export function roadmapKey(month: string | null) {
  return [...ROADMAP_KEY, month] as const;
}

/**
 * Everything the Roadmap page shows: who is looking, every item in the
 * default rank (with what the viewer may do with it, and for a developer its
 * Linear ids), the figures of one month ("2026-11"; the current month in
 * Belgium for null) and the dashboard's people. The month on screen stays
 * while the next one loads.
 */
export function useRoadmap(month: string | null) {
  return useQuery({
    queryKey: roadmapKey(month),
    queryFn: async (): Promise<RoadmapOverview> => {
      const { data } = await adminGetRoadmap(month ? { query: { month } } : undefined);
      logger.debug("[roadmap]", "overview", data?.month.month, data?.items.length, "items");
      return required(data, "roadmap");
    },
    placeholderData: (previous) => previous,
  });
}

/**
 * After a change to the roadmap: refetch every month's overview and, for a
 * change that is logged on the acting person, the activity feed. It returns
 * the overview's refetch, so the mutation stays pending until the page shows
 * the change (there are no optimistic updates).
 */
function refetchRoadmap(
  queryClient: ReturnType<typeof useQueryClient>,
  { logged = true }: { logged?: boolean } = {},
): Promise<void> {
  if (logged) {
    void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
  }
  return queryClient.invalidateQueries({ queryKey: ROADMAP_KEY });
}

/**
 * After a refused change to an item: refetch the overview, without waiting
 * and without the activity feed (nothing was logged). The item most likely
 * changed or went in the meantime, and the page then shows it as it is, with
 * the steps the server allows now, while the failure shows.
 */
function refetchRoadmapAfterRefusal(queryClient: ReturnType<typeof useQueryClient>): void {
  void refetchRoadmap(queryClient, { logged: false });
}

/**
 * Refetches the roadmap now, without waiting and without the activity feed.
 * The page never refetches on focus, while a developer also works the roadmap
 * and its triage from Claude: a dialog about something Claude may have handled
 * meanwhile (a triage entry) calls it as it opens, so it learns before
 * anything is typed.
 */
export function useRefetchRoadmap(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => void refetchRoadmap(queryClient, { logged: false }), [queryClient]);
}

/**
 * Adds an idea (developers): no month yet, and its creator follows it. A
 * refusal refetches too: the Linear ids may have gone on an item added from
 * Claude since the page loaded, or the viewer's role may have changed.
 */
export function useCreateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoadmapItemCreate): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "create", body.priority);
      const { data } = await adminCreateRoadmapItem({ body });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Changes an item's title, summary, priority or deadline: only the fields
 * given. A refusal refetches too (`refetchRoadmapAfterRefusal`).
 */
export function useUpdateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      itemId: string;
      change: RoadmapItemUpdate;
    }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "update", vars.itemId, Object.keys(vars.change));
      const { data } = await adminUpdateRoadmapItem({
        path: { item_id: vars.itemId },
        body: vars.change,
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Moves an item to another status: plan it (in a month), back to the ideas,
 * start it, back to planned, or finish it (with its actual hours). A refusal
 * refetches too: someone else most likely moved the item first.
 */
export function useSetRoadmapStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      itemId: string;
      change: RoadmapStatusChange;
    }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "status", vars.itemId, vars.change.status);
      const { data } = await adminSetRoadmapStatus({
        path: { item_id: vars.itemId },
        body: vars.change,
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * A developer's change to an item: its effort step, actual hours, month or
 * Linear ids. A refusal refetches too.
 */
export function useSetRoadmapEffort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      itemId: string;
      change: RoadmapDeveloperUpdate;
    }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "effort", vars.itemId, Object.keys(vars.change));
      const { data } = await adminSetRoadmapEffort({
        path: { item_id: vars.itemId },
        body: vars.change,
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Follows an item, or stops following it. Not logged, so the activity feed
 * stays. A refusal refetches too, without waiting: the item was most likely
 * finished or deleted in the meantime, and the page then drops its follow
 * button while the failure shows.
 */
export function useFollowRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; following: boolean }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", vars.following ? "follow" : "unfollow", vars.itemId);
      const { data } = await adminFollowRoadmapItem({
        path: { item_id: vars.itemId },
        body: { following: vars.following },
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient, { logged: false }),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Adds a blockage to an item: people, another item or an outside party, with
 * what they need to do. A refusal refetches too, without waiting: the item
 * may have been finished or deleted in the meantime.
 */
export function useAddRoadmapBlocker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      itemId: string;
      blocker: RoadmapBlockerCreate;
    }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "add blocker", vars.itemId);
      const { data } = await adminAddRoadmapBlocker({
        path: { item_id: vars.itemId },
        body: vars.blocker,
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Resolves one of an item's blockages. One resolved already stays as it is.
 * A refusal refetches too, without waiting: someone else may have removed it,
 * or finished the item.
 */
export function useResolveRoadmapBlocker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; blockerId: string }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "resolve blocker", vars.itemId, vars.blockerId);
      const { data } = await adminResolveRoadmapBlocker({
        path: { item_id: vars.itemId, blocker_id: vars.blockerId },
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/** Removes a blockage added by mistake, open or resolved. A refusal refetches too. */
export function useDeleteRoadmapBlocker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; blockerId: string }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "delete blocker", vars.itemId, vars.blockerId);
      const { data } = await adminDeleteRoadmapBlocker({
        path: { item_id: vars.itemId, blocker_id: vars.blockerId },
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Deletes an item that is not done. A refusal refetches too: someone else
 * may have deleted or finished it, and its details then close or show it as
 * it is.
 */
export function useDeleteRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string }) => {
      logger.debug("[roadmap]", "delete", vars.itemId);
      await adminDeleteRoadmapItem({ path: { item_id: vars.itemId } });
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Sets a month's budget in whole hours: a developer any month's, an owner
 * the current month's and later ones'. The month's figures come back.
 */
export function useSetRoadmapBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { month: string; budgetHours: number }): Promise<RoadmapMonth> => {
      logger.debug("[roadmap]", "budget", vars.month, vars.budgetHours);
      const { data } = await adminSetRoadmapBudget({
        path: { month: vars.month },
        body: { budget_hours: vars.budgetHours },
      });
      return required(data, "month");
    },
    onSuccess: () => refetchRoadmap(queryClient),
  });
}

/**
 * One item's comments. Under the roadmap's key, so every change to the
 * roadmap refetches an open thread too.
 */
export function roadmapCommentsKey(itemId: string) {
  return [...ROADMAP_KEY, "comments", itemId] as const;
}

/** An item's comments, oldest first, each saying whether the viewer may delete it. */
export function useRoadmapComments(itemId: string) {
  return useQuery({
    queryKey: roadmapCommentsKey(itemId),
    queryFn: async (): Promise<RoadmapComment[]> => {
      const { data } = await adminListRoadmapComments({ path: { item_id: itemId } });
      logger.debug("[roadmap]", "comments", itemId, data?.length);
      return required(data, "comments");
    },
  });
}

/**
 * Comments on an item that is not done, mentioning people who reach the
 * roadmap. The item's count changes, so the overview refetches with its
 * thread. A refusal refetches too, without waiting: the item may have been
 * finished or deleted in the meantime.
 */
export function useAddRoadmapComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      itemId: string;
      comment: RoadmapCommentCreate;
    }): Promise<RoadmapComment> => {
      logger.debug("[roadmap]", "comment", vars.itemId, vars.comment.mentions?.length ?? 0);
      const { data } = await adminAddRoadmapComment({
        path: { item_id: vars.itemId },
        body: vars.comment,
      });
      return required(data, "comment");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/** Deletes a comment: its author's own, or any for a developer. A refusal refetches too. */
export function useDeleteRoadmapComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; commentId: string }) => {
      logger.debug("[roadmap]", "delete comment", vars.itemId, vars.commentId);
      await adminDeleteRoadmapComment({
        path: { item_id: vars.itemId, comment_id: vars.commentId },
      });
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Passes an idea on to the triage, where a developer reviews it: for anyone
 * but a developer, who adds ideas straight to the roadmap. Nothing on the
 * roadmap changes for the person who proposed it, so only the activity feed
 * and, for a developer's own view, the overview refetch.
 */
export function useProposeRoadmapIdea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoadmapIdeaProposal) => {
      logger.debug("[roadmap]", "propose idea", body.priority ?? "no priority");
      await adminProposeRoadmapIdea({ body });
    },
    onSuccess: () => {
      void refetchRoadmap(queryClient);
    },
  });
}

/**
 * Accepts an open triage entry onto the roadmap as a new idea, by the rules
 * of a new idea, with its Linear ids and an optional effort (developers). The
 * entry leaves the triage. A refusal refetches too: another developer may
 * have handled the entry first.
 */
export function useAcceptRoadmapTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      entryId: string;
      item: RoadmapItemCreate;
    }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "accept triage entry", vars.entryId, vars.item.priority);
      const { data } = await adminAcceptRoadmapTriage({
        path: { entry_id: vars.entryId },
        body: vars.item,
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Closes an open triage entry without putting it on the roadmap, with an
 * optional note (developers). The close dialog tells the developer that
 * whoever submitted the entry reads the note, so a note goes out shared. A
 * refusal refetches too.
 */
export function useCloseRoadmapTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entryId: string; note: string }) => {
      logger.debug("[roadmap]", "close triage entry", vars.entryId);
      const note = vars.note.trim();
      await adminCloseRoadmapTriage({
        path: { entry_id: vars.entryId },
        ...(note ? { body: { note, share_note: true } } : {}),
      });
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Merges an open triage entry into an item already on the roadmap that is not
 * done (developers): the entry leaves the triage, linked to that item, and
 * whoever submitted an idea follows the item when they see the roadmap.
 * Answers the item. A refusal refetches too: another developer may have
 * handled the entry first, or the item may be done or gone.
 */
export function useMergeRoadmapTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entryId: string; itemId: string }): Promise<RoadmapItem> => {
      logger.debug("[roadmap]", "merge triage entry", vars.entryId, "into", vars.itemId);
      const { data } = await adminMergeRoadmapTriage({
        path: { entry_id: vars.entryId },
        body: { item_id: vars.itemId },
      });
      return required(data, "roadmap item");
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

/**
 * Deletes a triage entry for good, with what its submitter typed
 * (developers). A refusal refetches too: the entry may have gone already.
 */
export function useDeleteRoadmapTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entryId: string }) => {
      logger.debug("[roadmap]", "delete triage entry", vars.entryId);
      await adminDeleteRoadmapTriage({ path: { entry_id: vars.entryId } });
    },
    onSuccess: () => refetchRoadmap(queryClient),
    onError: () => refetchRoadmapAfterRefusal(queryClient),
  });
}

// ─── Apps ────────────────────────────────────────────────────────────────

/** `GET /apps`, which carries the sidebar's apps and their badges. Invalidate it exactly. */
export const APPS_KEY = ["apps"] as const;
/** The Bezettingsgraad app's latest snapshot, its job and whether a recompute can start. */
export const BEZETTING_SNAPSHOT_KEY = ["apps", "bezetting", "snapshot"] as const;
/** The meldingen with their shared read state. */
export const BEZETTING_MELDINGEN_KEY = ["apps", "bezetting", "meldingen"] as const;

/** One section of one snapshot: a snapshot never changes, so it is fetched once. */
export function bezettingSectionKey(snapshotId: string, key: string) {
  return ["apps", "bezetting", "section", snapshotId, key] as const;
}

/** A call's data, or an error when a call that throws on failure still gave none. */
function required<T>(data: T | undefined, what: string): T {
  if (data === undefined) {
    throw new Error(`The server returned no ${what}`);
  }
  return data;
}

/**
 * The apps the signed-in person may open, in the registry's order, each with
 * its unread meldingen; and the admins who grant access. An app someone may
 * not open is not in the list, so the sidebar never shows it.
 */
export function useApps() {
  return useQuery({ queryKey: APPS_KEY, queryFn: fetchApps });
}

async function fetchApps(): Promise<AppsOverview> {
  const { data } = await listApps();
  logger.debug(
    "[apps]",
    "list",
    data?.apps.map((app) => app.slug),
  );
  return required(data, "apps");
}

/**
 * Reads the apps the server lets the caller open now, past the cache: after a
 * view switched, so the bar knows whether the app page that is open is still
 * the new view's before it loads the page again.
 */
export function useFetchApps() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.fetchQuery({ queryKey: APPS_KEY, queryFn: fetchApps, staleTime: 0 }),
    [queryClient],
  );
}

/**
 * Bezettingsgraad's latest snapshot (null before the first recompute), the
 * running or last failed job, and whether Herbereken can start. A 403 means
 * the person may not open the app: it is never retried.
 */
export function useBezettingSnapshot({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: BEZETTING_SNAPSHOT_KEY,
    queryFn: async (): Promise<AppSnapshotSummary> => {
      const { data } = await getBezettingSnapshot();
      logger.debug("[bezetting]", "snapshot", data?.snapshot?.id ?? "none", data?.job?.status);
      return required(data, "snapshot summary");
    },
  });
}

/** The meldingen as the server holds them: unresolved, unread or read today. */
export function fetchBezettingMeldingen(): Promise<MeldingenList> {
  return listBezettingMeldingen().then(({ data }) => required(data, "meldingen"));
}

/** The meldingen query, which the app's meldingen store reads and keeps. */
export function useBezettingMeldingen({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: BEZETTING_MELDINGEN_KEY,
    queryFn: fetchBezettingMeldingen,
    enabled,
  });
}

/** One section of a snapshot, as stored. */
export async function fetchBezettingSection(
  snapshotId: string,
  key: string,
): Promise<Record<string, unknown>> {
  logger.debug("[bezetting]", "section", key);
  const { data } = await getBezettingSection({ path: { snapshot_id: snapshotId, key } });
  return required(data, "section");
}

/** Starts a recompute: 202 with the job, else an `ApiError` (409, 429 with `retryAfter`, 503). */
export async function startBezettingRecompute(): Promise<RecomputeAccepted> {
  logger.debug("[bezetting]", "recompute");
  const { data } = await recomputeBezetting();
  return required(data, "job");
}

/** A recompute job's state. */
export async function fetchBezettingJob(jobId: string): Promise<AppJob> {
  const { data } = await getBezettingJob({ path: { job_id: jobId } });
  return required(data, "job");
}

/** The Maandrapport app's latest snapshot, its job and whether a recompute can start. */
export const MAANDRAPPORT_SNAPSHOT_KEY = ["apps", "maandrapport", "snapshot"] as const;

/** One section of one Maandrapport snapshot: a snapshot never changes, so it is fetched once. */
export function maandrapportSectionKey(snapshotId: string, key: string) {
  return ["apps", "maandrapport", "section", snapshotId, key] as const;
}

/**
 * Maandrapport's latest snapshot (null before the first recompute), the
 * running or last failed job, and whether Herbereken can start. A 403 means
 * the person may not open the app: it is never retried.
 */
export function useMaandrapportSnapshot() {
  return useQuery({
    queryKey: MAANDRAPPORT_SNAPSHOT_KEY,
    queryFn: async (): Promise<AppSnapshotSummary> => {
      const { data } = await getMaandrapportSnapshot();
      logger.debug("[maandrapport]", "snapshot", data?.snapshot?.id ?? "none", data?.job?.status);
      return required(data, "snapshot summary");
    },
  });
}

/** One section of a Maandrapport snapshot, as stored. */
export async function fetchMaandrapportSection(
  snapshotId: string,
  key: string,
): Promise<Record<string, unknown>> {
  logger.debug("[maandrapport]", "section", key);
  const { data } = await getMaandrapportSection({ path: { snapshot_id: snapshotId, key } });
  return required(data, "section");
}

/** Starts a Maandrapport recompute: 202 with the job, else an `ApiError` (409, 429 with `retryAfter`, 503). */
export async function startMaandrapportRecompute(): Promise<RecomputeAccepted> {
  logger.debug("[maandrapport]", "recompute");
  const { data } = await recomputeMaandrapport();
  return required(data, "job");
}

/** A Maandrapport recompute job's state. */
export async function fetchMaandrapportJob(jobId: string): Promise<AppJob> {
  const { data } = await getMaandrapportJob({ path: { job_id: jobId } });
  return required(data, "job");
}

/** The view of one date range of one Maandrapport snapshot: it never changes, so it is fetched once. */
export function maandrapportViewKey(snapshotId: string, from: string, to: string) {
  return ["apps", "maandrapport", "view", snapshotId, from, to] as const;
}

/**
 * `GET /apps/maandrapport/snapshots/{id}/view?from&to`: every chapter for one range, as the renderer reads it
 * (`features/apps/maandrapport/contract.ts`). A range the snapshot does not allow answers 422 `invalid_range`.
 */
export async function fetchMaandrapportView(
  snapshotId: string,
  from: string,
  to: string,
): Promise<MaandrapportView> {
  logger.debug("[maandrapport]", "view", from, to);
  const { data } = await getMaandrapportView({
    path: { snapshot_id: snapshotId },
    query: { from, to },
  });
  return required(data, "view");
}

/** One detail behind a figure: kind, key, range and moment make it; a snapshot never changes. */
export function maandrapportDetailKey(
  snapshotId: string,
  kind: string,
  key: string,
  from: string,
  to: string,
  at: string,
) {
  return ["apps", "maandrapport", "detail", snapshotId, kind, key, from, to, at] as const;
}

/**
 * `GET /apps/maandrapport/snapshots/{id}/detail/{kind}?from&to&key[&at]`: the breakdown behind one figure. An
 * unknown kind or key answers 404 `detail_not_found`, an unknown client 404 `client_not_found`.
 */
export async function fetchMaandrapportDetail(
  snapshotId: string,
  request: { kind: string; key: string; from: string; to: string; at?: "this" | "last" },
): Promise<GetMaandrapportDetailResponse> {
  logger.debug("[maandrapport]", "detail", request.kind, request.key);
  const { data } = await getMaandrapportDetail({
    path: { snapshot_id: snapshotId, kind: request.kind },
    query: {
      from: request.from,
      to: request.to,
      key: request.key,
      ...(request.at ? { at: request.at } : {}),
    },
  });
  return required(data, "detail");
}

/**
 * One section of Bezettingsgraad's latest snapshot, through the same cache as the Bezettingsgraad page's own
 * loader (`bezettingSectionKey`): Maandrapport's Overzicht reads the fleet's key figure from it.
 */
export function useBezettingSection(
  snapshotId: string | null | undefined,
  key: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: bezettingSectionKey(snapshotId ?? "", key),
    queryFn: () => fetchBezettingSection(snapshotId ?? "", key),
    enabled: enabled && Boolean(snapshotId),
    staleTime: Infinity,
    retry: (failureCount, error) => {
      const status = (error as Partial<ApiError> | null)?.status ?? 0;
      return failureCount < 2 && (status === 0 || status >= 500);
    },
  });
}

/** Marks meldingen read or unread for everyone; the full list comes back, with the keys ignored. */
export async function saveBezettingMeldingen(
  action: "read" | "unread",
  keys: readonly string[],
): Promise<MeldingenUpdate> {
  logger.debug("[bezetting]", "meldingen", action, keys.length);
  const body = { keys: [...keys] };
  const { data } =
    action === "read"
      ? await markBezettingMeldingenRead({ body })
      : await markBezettingMeldingenUnread({ body });
  return required(data, "meldingen");
}
